import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Dialog,
  CircularProgress,
  Backdrop,
  DialogContentText,
  Autocomplete,
} from "@mui/material";
import PropTypes, { number } from "prop-types";
import { AddUserApi, checkPhoneNumber, uploadFile } from "api/UserAPI";
import "dayjs/locale/vi";
import { makeStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { loadSelectRole } from "api/rolesService";
import Swal from "sweetalert2";
import { commonTextLabelStyle } from "styles/muiStyles";
import { useColorTheme } from "../../../context/ColorThemeContext";

function AddUser({ refreshDataAfterChage, open, onClose }) {
  const colors = useColorTheme();

  const useStyles = makeStyles({
    input: {
      "&::placeholder": {
        fontStyle: "italic !important",
      },
    },
  });
  const classes = useStyles();

  const [responseData, setResponseData] = useState({
    message: "",
    status: Number,
  });
  const [checkExisted, setCheckExisted] = useState({
    message: "",
    status: Number,
  });
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({
    fullname: "",
    username: "",
    email: "",
    phoneNumber: "",
    roleId: "",
    departmentId: "",
    administrativeAreaId: "",
    partnerId: "",
    isAdmin: false,
    password: "",
    cccd: "",
    confirmPassword: "",
    hashtagUser: "",
    fullNameLeader1: "",
    fullNameLeader2: "",
  });
  const [selectedRole, setSelectedRole] = useState(null);
  const [optionRoles, setOptionRoles] = useState([]);

  const fetchOptionRoles = async () => {
    const response = await loadSelectRole("add");
    if (response.status === 200) {
      setOptionRoles(response.object);
    }
  };

  useEffect(() => {
    if (open) {
      fetchOptionRoles();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));
  };

  const checkExistedPhoneNumber = async () => {
    const res = await checkPhoneNumber(userData.phoneNumber);
    if (res.status === 400 && !res.object) {
      setCheckExisted({
        message: res.message,
        status: 400,
      });
    }
  };

  // Xử lý chọn file QR và gọi API
  const handleQRUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const response = await uploadFile(file);
      if (response.status === 200 && response.object) {
        const { hoTen, soCCCD, ngaySinh } = response.object;

        // Convert ngaySinh to YYYY-MM-DD format
        const formattedNgaySinh = ngaySinh
          ? (() => {
              const [day, month, year] = ngaySinh.split("/");
              return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`; // Chuyển từ DD/MM/YYYY sang YYYY-MM-DD
            })()
          : "";

        setUserData((prevData) => ({
          ...prevData,
          fullname: hoTen || prevData.fullname,
          cccd: soCCCD || prevData.cccd,
          birthday: formattedNgaySinh || prevData.birthday,
        }));
        Swal.fire({
          title: "Quét QR thành công",
          icon: "success",
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        Swal.fire({
          title: "Quét QR thất bại",
          text: response.message || "Không thể đọc dữ liệu từ mã QR",
          icon: "error",
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Lỗi",
        text: "Lỗi khi xử lý mã QR",
        icon: "error",
        showConfirmButton: false,
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const re = /(84|0[3|5|7|8|9])+([0-9]{8})\b/;
    return re.test(phoneNumber);
  };

  const handleClose = () => {
    setUserData({
      fullname: "",
      username: "",
      email: "",
      phoneNumber: "",
      roleId: "",
      departmentId: "",
      administrativeAreaId: "",
      partnerId: "",
      isAdmin: false,
      cccd: "",
      hashtagUser: "",
      fullNameLeader1: "",
      fullNameLeader2: "",
    });
    setSelectedRole(null);
    setResponseData({
      message: "",
      status: number,
    });
    onClose();
  };

  const handleAddUser = () => {
    setLoading(true);
    const birthdayDate = new Date(userData.birthday); // Convert nếu là string ISO
    if (
      userData.fullname === "" ||
      userData.username === "" ||
      userData.password === "" ||
      userData.confirmPassword === "" ||
      userData.roleId === "" ||
      userData.departmentId === "" ||
      userData.administrativeAreaId === ""
    ) {
      setResponseData({
        message: "Nhập thông tin bắt buộc",
        status: 400,
      });
      setLoading(false);
    } else if (userData.password !== userData.confirmPassword) {
      setResponseData({
        message: "Mật khẩu và nhập lại mật khẩu không khớp",
        status: 400,
      });
      setLoading(false);
    } else if (userData.email !== "" && !validateEmail(userData.email)) {
      setResponseData({
        message: "Email không hợp lệ",
        status: 400,
      });
      setLoading(false);
    } else if (userData.phoneNumber !== "" && !validatePhoneNumber(userData.phoneNumber)) {
      setResponseData({
        message: "Số điện thoại không hợp lệ",
        status: 400,
      });
      setLoading(false);
    } else if (userData.username.match(/[^A-Za-z0-9_]/)) {
      setResponseData({
        message: "Tên đăng nhập không được chứa kí tự đặc biệt",
        status: 400,
      });
      setLoading(false);
    } else if (checkExisted.message === "Số điện thoại này đã được sử dụng.") {
      setLoading(false);
    } else if (birthdayDate > new Date()) {
      setResponseData({
        message: "Ngày sinh không được lớn hơn ngày hiện tại",
        status: 400,
      });
      setLoading(false);
    } else {
      AddUserApi(userData).then((data) => {
        if (data) {
          setResponseData(data);
          setLoading(false);
          if (data.status === 200) {
            Swal.fire({
              title: "Thao tác thành công",
              icon: "success",
              showConfirmButton: false,
              timer: 2000,
            });
            handleClose();
            refreshDataAfterChage();
          } else {
            Swal.fire({
              title: data.message,
              icon: "error",
              showConfirmButton: false,
              timer: 2000,
            });
          }
        } else {
          setResponseData({
            message: "Thao tác thất bại",
            status: 400,
          });
        }
      });
    }
  };

  return (
    <Dialog
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xxxl"
      open={open}
      onClose={() => {
        setResponseData({
          message: null,
          status: null,
        });
        handleClose();
        onClose();
      }}
      autoComplete="off"
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.popupBackground || "var(--blue-blue-100)",
          color: colors.popupText || "white !important",
          padding: "13px 20px",
          fontWeight: "bold",
          fontSize: "18px",
          position: "relative",
        }}
      >
        Thêm mới người dùng
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "white !important",
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: "10px !important" }}>
        <DialogContentText id="alert-dialog-description">
          <Grid container spacing={3} mb={2} mt={2}>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label={
                  <span>
                    Tên đăng nhập
                    <span style={{ color: "#FF0000" }}>*</span>
                  </span>
                }
                className="form-field"
                fullWidth
                name="username"
                value={userData.username}
                onChange={handleChange}
                inputProps={{ maxLength: 20 }}
                error={
                  responseData.message === "Tên đăng nhập đã tồn tại" ||
                  responseData.message === "Tên đăng nhập không được chứa kí tự đặc biệt" ||
                  (userData.username === "" && responseData.message === "Nhập thông tin bắt buộc")
                }
                helperText={
                  responseData.message === "Tên đăng nhập đã tồn tại" ||
                  responseData.message === "Tên đăng nhập không được chứa kí tự đặc biệt" ||
                  (userData.username === "" && responseData.message === "Nhập thông tin bắt buộc")
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                InputProps={{ classes: { input: classes.input }, inputProps: { maxLength: 50 } }}
                id="outlined-required"
                label={
                  <span>
                    Họ tên
                    <span style={{ color: "#FF0000" }}>*</span>
                  </span>
                }
                style={{ width: "100%" }}
                className="form-field"
                fullWidth
                name="fullname"
                value={userData.fullname}
                onChange={handleChange}
                error={
                  userData.fullname === "" && responseData.message === "Nhập thông tin bắt buộc"
                }
                helperText={
                  userData.fullname === "" && responseData.message === "Nhập thông tin bắt buộc"
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Email"
                className="form-field"
                fullWidth
                name="email"
                value={userData.email}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
                error={responseData.message === "Email không hợp lệ"}
                helperText={
                  responseData.message === "Email không hợp lệ" ? responseData.message : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Số điện thoại"
                className="form-field"
                fullWidth
                name="phoneNumber"
                value={userData.phoneNumber}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  setUserData((prevData) => ({
                    ...prevData,
                    phoneNumber: onlyDigits,
                    hashtagUser: onlyDigits ? `$Q9_${onlyDigits}` : "",
                  }));
                  handleChange(e);
                  if (onlyDigits.length === 10) checkExistedPhoneNumber(onlyDigits);
                }}
                inputProps={{ maxLength: 10 }}
                error={
                  responseData.message === "Số điện thoại không hợp lệ" ||
                  checkExisted.message === "Số điện thoại này đã được sử dụng."
                }
                helperText={
                  checkExisted.message === "Số điện thoại này đã được sử dụng."
                    ? checkExisted.message
                    : responseData.message === "Số điện thoại không hợp lệ"
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="CCCD"
                className="form-field"
                fullWidth
                name="cccd"
                value={userData.cccd}
                onChange={handleChange}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Ngày sinh"
                className="form-field"
                fullWidth
                name="birthday"
                value={userData.birthday}
                onChange={handleChange}
                type="date"
                error={responseData.message === "Ngày sinh không được lớn hơn ngày hiện tại"}
                helperText={
                  responseData.message === "Ngày sinh không được lớn hơn ngày hiện tại"
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label={
                  <span>
                    Mật khẩu
                    <span style={{ color: "#FF0000" }}>*</span>
                  </span>
                }
                className="form-field"
                fullWidth
                name="password"
                type="password"
                value={userData.password}
                onChange={handleChange}
                error={
                  userData.password === "" && responseData.message === "Nhập thông tin bắt buộc"
                }
                helperText={
                  userData.password === "" && responseData.message === "Nhập thông tin bắt buộc"
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label={
                  <span>
                    Nhập lại mật khẩu
                    <span style={{ color: "#FF0000" }}>*</span>
                  </span>
                }
                className="form-field"
                fullWidth
                name="confirmPassword"
                type="password"
                value={userData.confirmPassword}
                onChange={handleChange}
                error={
                  (userData.confirmPassword === "" &&
                    responseData.message === "Nhập thông tin bắt buộc") ||
                  responseData.message === "Mật khẩu và nhập lại mật khẩu không khớp"
                }
                helperText={
                  responseData.message === "Mật khẩu và nhập lại mật khẩu không khớp" ||
                  (userData.confirmPassword === "" &&
                    responseData.message === "Nhập thông tin bắt buộc")
                    ? responseData.message
                    : ""
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={6} xl={6}>
              <Autocomplete
                options={optionRoles}
                getOptionLabel={(option) => `${option.name ? `${option.name}` : ""}`}
                onChange={(event, newValue) => {
                  setSelectedRole(newValue);
                  if (newValue !== null) setUserData({ ...userData, roleId: newValue.id });
                }}
                value={selectedRole || null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <span>
                        Vai trò<span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder="Chọn vai trò"
                    error={
                      selectedRole === null || responseData.message === "Nhập thông tin bắt buộc"
                    }
                    helperText={
                      selectedRole === null || responseData.message === "Nhập thông tin bắt buộc"
                        ? responseData.message
                        : ""
                    }
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={6} sx={{ display: "none" }}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Mã số user"
                className="form-field"
                placeholder="Mã sinh tự động cú pháp: $Q9_[số điện thoại User]"
                fullWidth
                name="hashtagUser"
                value={userData.hashtagUser}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Tên lãnh đạo trực tiếp 1"
                className="form-field"
                fullWidth
                name="fullNameLeader1"
                value={userData.fullNameLeader1}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={6}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                  color: "primary",
                }}
                sx={commonTextLabelStyle}
                label="Tên lãnh đạo trực tiếp 2"
                className="form-field"
                fullWidth
                name="fullNameLeader2"
                value={userData.fullNameLeader2}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          paddingTop: "0px !important",
          mb: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Nút mới nằm ngoài cùng bên trái */}
        <Button
          sx={{
            "&.MuiButton-root.MuiButton-contained": {
              boxShadow: "none !important",
              fontWeight: "normal",
              height: "36px !important",
              maxHeight: "36px !important",
              marginLeft: "24px",
            },
            "&.MuiButton-root": {
              minHeight: "36px !important",
            },
            position: "absolute",
            left: 0,
          }}
          style={{
            fontWeight: "normal",
            width: "21.5%",
            backgroundColor: colors.popupBackground || "var(--blue-blue-100)",
            color: colors.popupText || "white",
          }}
          variant="contained"
          component="label"
        >
          Quét QR CCCD
          <input type="file" hidden accept="image/*" onChange={handleQRUpload} />
        </Button>

        {/* Hai nút Đóng và Lưu giữ nguyên vị trí giữa */}
        <Button
          sx={{
            "&.MuiButton-root.MuiButton-contained": {
              boxShadow: "none !important",
              fontWeight: "normal",
              height: "36px !important",
              maxHeight: "36px !important",
            },
            "&.MuiButton-root": {
              minHeight: "36px !important",
            },
          }}
          style={{
            width: "18%",
            borderColor: colors.popupBackground || "var(--blue-blue-100)",
            color: colors.popupBackground,
            fontWeight: "normal",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            setResponseData({
              message: null,
              status: null,
            });
          }}
        >
          Đóng
        </Button>
        <Button
          sx={{
            "&.MuiButton-root.MuiButton-contained": {
              boxShadow: "none !important",
              fontWeight: "normal",
              height: "36px !important",
              maxHeight: "36px !important",
            },
            "&.MuiButton-root": {
              minHeight: "36px !important",
            },
          }}
          style={{
            fontWeight: "normal",
            width: "18%",
            backgroundColor: colors.popupBackground || "var(--blue-blue-100)",
            color: colors.popupText || "white",
          }}
          variant="contained"
          onClick={handleAddUser}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
}
AddUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshDataAfterChage: PropTypes.func.isRequired,
};

export default AddUser;
